import { DEFAULT_STATS, DEFAULT_BASE_STATS, DEFAULT_MINING, DEFAULT_SKINNING, DEFAULT_COOKING, DEFAULT_FISHING, type CharacterStats, type BaseStats, type MiningState, type SkinningState, type CookingState, type FishingState, type ProfessionEntry, type ItemMods } from "@ghostland/shared";

export interface PlayerStatsState {
  stats: CharacterStats;
  baseStats: BaseStats;
  bankMoney: number;
  mining: MiningState;
  skinning: SkinningState;
  cooking: CookingState;
  fishing: FishingState;
  professions: ProfessionEntry[];
  /** Flat armor from worn gear (added on top of strength-derived armor). */
  gearArmor: number;
  /** Per-stat level bonus from worn gear (leather armor +1). */
  statBonus: { strength: number; dex: number; agility: number };
  /** Rolled armored bonus points on the WORN top/bottom (for the profile gear tooltips). */
  wornMods?: { top?: ItemMods; bottom?: ItemMods };
}
type Listener = (s: PlayerStatsState) => void;

/**
 * Client mirror of the local player's base stats + bank money, fed by the server's
 * private `PlayerStats` message. The HUD (health bar) and inventory panel subscribe.
 */
class StatsStore {
  private state: PlayerStatsState = { stats: { ...DEFAULT_STATS }, baseStats: structuredClone(DEFAULT_BASE_STATS), bankMoney: 0, mining: { ...DEFAULT_MINING }, skinning: { ...DEFAULT_SKINNING }, cooking: { ...DEFAULT_COOKING }, fishing: { ...DEFAULT_FISHING }, professions: [], gearArmor: 0, statBonus: { strength: 0, dex: 0, agility: 0 } };
  private readonly listeners = new Set<Listener>();

  get(): PlayerStatsState { return this.state; }

  set(state: PlayerStatsState): void {
    this.state = state;
    for (const fn of this.listeners) fn(this.state);
  }

  /** The player's level in a profession they've learned, or 0 if they don't have it. */
  professionLevel(id: string): number {
    return this.state.professions.find((p) => p.id === id)?.level ?? 0;
  }

  /** Subscribe; immediately invoked with the current value. Returns unsubscribe. */
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.state);
    return () => this.listeners.delete(fn);
  }
}

export const statsStore = new StatsStore();
